import { Component, OnInit, ViewEncapsulation, computed, inject } from "@angular/core";
import {
    AssetManagementService,
    AssetLifecycle,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { formatIsoDate } from "@methods/CommonMethods";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { finalize, firstValueFrom } from "rxjs";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixIconButtonComponent } from "@app/shared/molecules/cerrix-icon-button/cerrix-icon-button.component";
import { CerrixPageFooterComponent } from "@app/shared/cerrix-page-footer/cerrix-page-footer.component";
import { WorkflowStepItemModel } from "@app/shared/cerrix-workflow-bar/models/workflow-step-item";
import { WorkflowStepStatus } from "@app/shared/cerrix-workflow-bar/models/workflow-step-status.enum";
import { CerrixHeaderComponent } from "@app/shared/organisms/cerrix-header/cerrix-header.component";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CerrixTextareaGroupComponent } from "@app/shared/molecules/cerrix-textarea-group/cerrix-textarea-group.component";
import { CerrixDatepickerGroupComponent } from "@app/shared/molecules/cerrix-datepicker-group/cerrix-datepicker-group.component";
import { CerrixTreeItemPickerSingleComponent } from "@app/shared/organisms/cerrix-tree-item-picker-single/cerrix-tree-item-picker-single.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { HttpClient } from "@angular/common/http";
import { AssetOwnerSelectorComponent } from "@app/assetmanagement/components/asset-owner-selector/asset-owner-selector.component";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";

@Component({
    selector: "app-assetmanagement-detail-register",
    templateUrl: "./assetmanagement-detail-register.component.html",
    styleUrls: ["./assetmanagement-detail-register.component.scss"],
    imports: [
        CerrixPageFooterComponent,
        CerrixButtonComponent,
        CerrixIconButtonComponent,
        CerrixHeaderComponent,
        CerrixTextareaGroupComponent,
        CerrixDatepickerGroupComponent,
        CerrixTreeItemPickerSingleComponent,
        AssetOwnerSelectorComponent,
        MenuSystemDirective,
        TranslocoPipe,
        CerrixInputValidationRequiredDirective,
    ],
    providers: [CerrixInputValidationRegistryService, provideTranslocoScope("assetmanagement")],
    encapsulation: ViewEncapsulation.None,
})
export class AssetManagementDetailRegisterComponent implements OnInit {
    tab: TabModel;
    id: string;

    model: AssetManagementModel;

    private workflowGeneralInformation = "generalInformation";
    private workflowAssetType = "assetType";
    private workflowOverview = "Overview";
    public workflowSteps: WorkflowStepItemModel[] = [];

    public breadcrumbs: string[] = [];

    public saveButtonEnabled = true;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _toastService = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _standingDataDS = inject(StandingdataDataService);
    private _transloco = inject(TranslocoService);
    private _http = inject(HttpClient);

    public organizationOwnerList: CerrixTreeItem[] = [];

    public users: IdGuidNameCombination[] = [];

    protected validationErrors = computed(() => {
        return this._validationRegistry.hasAnyErrors();
    });

    async ngOnInit() {
        // Ensure the scope JSON is loaded before the synchronous translate() calls below,
        // otherwise they resolve to the raw key while the lazy scope is still loading.
        await firstValueFrom(
            this._transloco.load(`assetmanagement/${this._transloco.getActiveLang()}`),
        );

        this.workflowSteps = [
            {
                id: this.workflowGeneralInformation,
                name: this._transloco.translate(
                    "assetmanagement.detail.workflowGeneralInformation",
                ),
                status: WorkflowStepStatus.Active,
                infoTitle: "",
            },
            {
                id: this.workflowAssetType,
                name: this._transloco.translate("assetmanagement.detail.workflowAssetType"),
                status: WorkflowStepStatus.Disabled,
                infoTitle: "",
            },
            {
                id: this.workflowOverview,
                name: this._transloco.translate("assetmanagement.detail.workflowOverview"),
                status: WorkflowStepStatus.Disabled,
                infoTitle: "",
            },
        ];

        this.breadcrumbs = [
            this._transloco.translate("assetmanagement.detail.breadcrumbWorkspace"),
        ];

        this.model = new AssetManagementModel();
        this.model.name = "";
        this.model.description = "";
        this.model.lifecycle = { dateInUse: null, dateOutOfUse: null };
        this.model.ownershipInformation = { owningOrganizationId: null, ownerId: null };
        this.breadcrumbs.push(
            this._transloco.translate("assetmanagement.detail.breadcrumbNewAsset"),
        );

        this.organizationOwnerList = await firstValueFrom(
            this._standingDataDS.getOrganizationsIncludingSelected(
                ModuleType.AssetManagement,
                null,
                true,
            ),
        );

        this.users = await this.getUsers();

        this.tab.showLoader = false;
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        let params = "";

        if (this.model.ownershipInformation.ownerId) {
            params = `?selected=${this.model.ownershipInformation.ownerId}`;
        }

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/list` + params),
        );
    }

    protected onOwnerIdChange(ownerId: string): void {
        this.model.ownershipInformation = {
            ...this.model.ownershipInformation,
            ownerId,
        };
    }

    async save() {
        if (!this.validateFields()) {
            return;
        }

        if (this.model.reference === undefined) {
            this.saveButtonEnabled = false;
            const savingPrompt = this._promptService.loader(
                this._transloco.translate("assetmanagement.detail.savingChanges"),
            );

            const lifecycleModel: AssetLifecycle = {
                dateInUse: formatIsoDate(this.model.lifecycle.dateInUse),
                dateOutOfUse: formatIsoDate(this.model.lifecycle.dateOutOfUse),
            };
            const storedAsset = await firstValueFrom(
                this._ds
                    .registerAssetVbeta({
                        assetName: this.model.name,
                        description: this.model.description,
                        organizationId: this.model.ownershipInformation.owningOrganizationId,
                        ownerId: this.model.ownershipInformation?.ownerId ?? null,
                        lifecycle: lifecycleModel,
                    })
                    .pipe(finalize(() => savingPrompt.close())),
            );

            this.tab.id = storedAsset.guid;
            this._tabService.refresh(this._pages.AssetManagementOverview);
            this._toastService.success(
                this._transloco.translate("assetmanagement.detail.updateCompleted"),
            );
        }
    }

    public cancel(): void {
        this.tab.close();
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._transloco.translate(
                                "assetmanagement.detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._transloco.translate("assetmanagement.detail.saveFailed"),
            );
            return false;
        }
        return true;
    }
}
